import React, { useState, useEffect, useCallback } from 'react'
import { Header } from './components/Header'
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router'

import CommonAssortmentOverview from './pages/common-overview/CommonAssortmentOverview'
import { CommonAssortment } from './pages/common/CommonAssortment'
import { FileStreamOverview } from './pages/file-stream-overview/FileStreamOverview'
import { FileStream } from './pages/file-stream/FileStream'
import { InstallationsOverview } from './pages/installations/InstallationsOverview'
import { InstallationView } from './pages/installations/InstallationView'
import { useRemoteData } from 'lib/RemoteData'
import { API_HOST, VERSION } from 'lib'
import { SRD } from 'srd'
import { CommonAssortmentEdit } from './pages/common/CommonAssortmentEdit'
import { CommonAssortmentCreate } from './pages/common/CommonAssortmentCreate'
import { Status } from './pages/status/Status'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { ManualActionInboxEntry } from './services/ManualActionService'

function Root() {
  const [selectedCountry, setSelectedCountry] = useLocalStorageState('assortmentManagementSelectedCountry', 'DE')

  const accessibleCountriesUrl = `${API_HOST}/bff/countries`
  const { data: contextCountriesData } = useRemoteData<string[]>(accessibleCountriesUrl)

  const { data: manualActionsData } = useRemoteData<ManualActionInboxEntry[]>(
    `${API_HOST}/v1/manual-actions?country=${selectedCountry}`,
  )
  const manualActionsCount = SRD.isSuccess(manualActionsData)
    ? (manualActionsData.data as ManualActionInboxEntry[]).length
    : 0

  const accessibleCountries = SRD.isSuccess(contextCountriesData) ? (contextCountriesData.data as string[]) : []
  const country = {
    accessibleCountries,
    selectCountry: setSelectedCountry,
  }

  return (
    <SelectedCountryContext.Provider value={{ selectedCountry }}>
      <Header country={country} manualActionsCount={manualActionsCount} />
      <div className="mt-md mx-md">
        <Outlet />
      </div>
      <div className="mx-md">Version: {VERSION}</div>
    </SelectedCountryContext.Provider>
  )
}

export const routes = [
  {
    path: '/',
    element: <Root />,
    children: [
      {
        path: '/common-assortment/new',
        element: <CommonAssortmentCreate />,
      },
      {
        path: '/common-assortment/:id',
        element: <CommonAssortment />,
      },
      {
        path: '/common-assortment/:id/:filetype',
        element: <CommonAssortment />,
      },
      {
        path: '/common-assortment/:id/edit',
        element: <CommonAssortmentEdit />,
      },
      {
        path: '/common-assortment',
        element: <CommonAssortmentOverview />,
      },
      {
        path: '/file-stream/:id',
        element: <FileStream />,
      },
      {
        path: '/file-stream',
        element: <FileStreamOverview />,
      },
      {
        path: '/file-stream/file-type/:filetype',
        element: <FileStreamOverview />,
      },
      {
        path: '/installation/:id',
        element: <InstallationView />,
      },
      {
        path: '/installation/:id/:filetype',
        element: <InstallationView />,
      },
      {
        path: '/installations',
        element: <InstallationsOverview />,
      },
      {
        path: '/status',
        element: <Status />,
      },
      {
        path: '/',
        element: <CommonAssortmentOverview />,
      },
      {
        path: '*',
        element: <h1>404</h1>,
      },
    ],
  },
]

function useLocalStorageState(localStorageKey: string, defaultValue: string): [string, (_: string) => void] {
  const existingValue = localStorage.getItem(localStorageKey)
  if (existingValue) {
    defaultValue = existingValue
  }

  const [value, setValue] = useState<string>(defaultValue)

  const setValueAndLocalStorage = useCallback(
    (newValue: string) => {
      localStorage.setItem(localStorageKey, newValue)
      setValue(newValue)
    },
    [localStorageKey],
  )

  useEffect(() => {
    const localStorageValue = localStorage.getItem(localStorageKey)
    if (localStorageValue) {
      setValue(localStorageValue)
    } else {
      setValueAndLocalStorage(defaultValue)
    }
  }, [defaultValue, localStorageKey, setValueAndLocalStorage])

  return [value, setValueAndLocalStorage]
}

export function App() {
  const router = createBrowserRouter(routes)
  return <RouterProvider router={router} />
}
